import {
  createContext,
  type FormEvent,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { Session } from '@supabase/supabase-js'
import { Mail, ShieldCheck } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useI18n } from '../../lib/i18n'
import { track } from '../../lib/analytics'

type AuthStep = 'email' | 'otp'
type AuthContextValue = { session: Session | null; ready: boolean; openSignIn: () => void }
const AuthContext = createContext<AuthContextValue | null>(null)
export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('AuthGate missing')
  return value
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { locale, t } = useI18n()
  const [session, setSession] = useState<Session | null>(null)
  const [ready, setReady] = useState(false)
  const [step, setStep] = useState<AuthStep>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [pending, setPending] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [signInOpen, setSignInOpen] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const demoMode = new URLSearchParams(window.location.search).get('demo') === '1'

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data }) => setSession(data.session))
      .catch(() => setSession(null))
      .finally(() => setReady(true))

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      setReady(true)
      if (nextSession) setSignInOpen(false)
    })

    return () => data.subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!signInOpen) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSignInOpen(false)
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [signInOpen])

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = window.setInterval(() => setCooldown((value) => Math.max(0, value - 1)), 1000)
    return () => window.clearInterval(timer)
  }, [cooldown])

  async function sendOtp() {
    if (pending || cooldown > 0) return
    setPending(true)
    setMessage(null)
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: true },
    })
    setPending(false)
    if (error) {
      setMessage(t('authFailed'))
      return
    }
    setStep('otp')
    setCooldown(60)
    setMessage(t('codeSent'))
  }

  async function requestOtp(event: FormEvent) {
    event.preventDefault()
    await sendOtp()
  }

  async function verifyOtp(event: FormEvent) {
    event.preventDefault()
    setPending(true)
    setMessage(null)
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: otp.trim(),
      type: 'email',
    })
    setPending(false)
    if (error) setMessage(t('authFailed'))
    else track('signed_in', locale)
  }

  const authValue = useMemo(
    () => ({
      session: demoMode ? null : session,
      ready: demoMode || ready,
      openSignIn: () => {
        setStep('email')
        setOtp('')
        setMessage(null)
        setSignInOpen(true)
      },
    }),
    [demoMode, ready, session],
  )
  return (
    <AuthContext.Provider value={authValue}>
      {children}
      {signInOpen && !session && !demoMode && (
        <div
          className="modal-scrim auth-scrim"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSignInOpen(false)
          }}
        >
          <section
            className="auth-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="auth-title"
          >
            <button
              className="auth-close"
              type="button"
              onClick={() => setSignInOpen(false)}
              aria-label={t('close')}
            >
              ×
            </button>
            <span className="auth-emblem" aria-hidden="true">
              <ShieldCheck />
            </span>
            <p className="eyebrow gold">{t('welcomeTo')}</p>
            <h1 id="auth-title">Clash Cards</h1>
            <p className="auth-intro">{t('authIntro')}</p>

            <form onSubmit={step === 'email' ? requestOtp : verifyOtp}>
              <label htmlFor={step === 'email' ? 'email' : 'otp'}>
                {step === 'email' ? t('email') : t('otp')}
              </label>
              <div className="auth-input">
                <Mail aria-hidden="true" />
                {step === 'email' ? (
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                ) : (
                  <input
                    id="otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={otp}
                    onChange={(event) => setOtp(event.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    required
                  />
                )}
              </div>
              <button className="auth-submit" type="submit" disabled={pending}>
                {pending ? t('wait') : step === 'email' ? t('sendCode') : t('loginContinue')}
              </button>
            </form>
            {step === 'otp' && (
              <div className="auth-secondary-actions">
                <button
                  className="auth-back"
                  type="button"
                  onClick={() => {
                    setStep('email')
                    setOtp('')
                    setCooldown(0)
                    setMessage(null)
                  }}
                >
                  {t('changeEmail')}
                </button>
                <button
                  className="auth-back"
                  type="button"
                  disabled={pending || cooldown > 0}
                  onClick={() => void sendOtp()}
                >
                  {cooldown > 0 ? t('resendIn', { count: cooldown }) : t('resendCode')}
                </button>
              </div>
            )}
            {message && (
              <p className="auth-message" role="status">
                {message}
              </p>
            )}
            <p className="auth-legal">
              {t('agreePrefix')}{' '}
              <a href="/terms.html" target="_blank" rel="noopener noreferrer">
                {t('terms')}
              </a>{' '}
              {t('and')}{' '}
              <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
                {t('privacy')}
              </a>
              .
            </p>
          </section>
        </div>
      )}
    </AuthContext.Provider>
  )
}
